/* eslint-disable no-untranslated-string/no-untranslated-string */
import { DateTime } from 'luxon';

export const ETimeFormat = {
  TIME_12: 'hh:mm a',
  TIME_24: 'HH:mm',
  TIME_SECOND_24: 'HH:mm:ss',
} as const;

export type ETimeFormat = (typeof ETimeFormat)[keyof typeof ETimeFormat];

export const ETimeNumericFormat = {
  TIME_12: 12,
  TIME_24: 24,
} as const;

// Allow any number but suggest using ETimeNumericFormat constants
export type ETimeNumericFormat = (typeof ETimeNumericFormat)[keyof typeof ETimeNumericFormat];

export type TimeFormatType =
  | typeof ETimeFormat.TIME_24
  | typeof ETimeFormat.TIME_12
  | typeof ETimeFormat.TIME_SECOND_24;

class DateTimeHelper {
  dt = DateTime;

  timeFormats = [ETimeFormat.TIME_24, ETimeFormat.TIME_12];

  getDateFromTime(time: string, inServerLocale = false) {
    return this.dt.fromFormat(
      time,
      this.getTimeFormat(time),
      inServerLocale ? { locale: 'en-US' } : undefined,
    );
  }

  getTimeFromFormat(value: string, timeFormat: ETimeNumericFormat, fromFormat?: string) {
    const _fromFormat = fromFormat ?? this.getTimeFormat(value);
    return this.dt
      .fromFormat(value, _fromFormat, {
        locale: 'en-US',
      })
      .toFormat(this.setTimeFormat(timeFormat));
  }

  getDateFromISO(date: string, setZone = false) {
    return this.dt.fromISO(date, { setZone });
  }

  getDateFromJS(date: Date) {
    return this.dt.fromJSDate(date);
  }

  getTime(
    value: string,
    timeFormat: ETimeNumericFormat,
    setZone = false,
    zone?: string,
    inServerLocale = false,
  ) {
    return this.dt
      .fromISO(value, { setZone, zone })
      .toFormat(this.setTimeFormat(timeFormat), inServerLocale ? { locale: 'en-US' } : undefined);
  }

  getTimeFromDate(value: DateTime, timeFormat: ETimeNumericFormat) {
    return value.toFormat(this.setTimeFormat(timeFormat));
  }

  isValidFormat(value: string, fromFormat: TimeFormatType) {
    return this.dt.fromFormat(value, fromFormat).isValid;
  }

  private setTimeFormat(timeFormat: number): TimeFormatType {
    if (timeFormat) {
      if (timeFormat === 12) {
        return ETimeFormat.TIME_12;
      } else if (timeFormat === 24) {
        return ETimeFormat.TIME_24;
      }
    }
    return ETimeFormat.TIME_12;
  }

  private getTimeFormat(date: string): TimeFormatType {
    if (date.includes('AM') || date.includes('PM') || date.includes(' ')) {
      return ETimeFormat.TIME_12;
    } else {
      return ETimeFormat.TIME_24;
    }
  }
}

export const dateTimeHelper = new DateTimeHelper();
